import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const PERIODS  = ['FY2024-25', 'FY2023-24'];
const FORMATS  = ['BRSR', 'GHG Protocol', 'GRI', 'Internal', 'CBAM Export'];
const FORMAT_DESC = {
  BRSR: 'India SEBI BRSR Principle 6 — Environmental disclosures',
  'GHG Protocol': 'GHG Protocol Corporate Standard — Scope 1, 2, 3',
  GRI: 'GRI 305: Emissions, GRI 306: Waste',
  Internal: 'Internal management report — all metrics',
  'CBAM Export': 'EU Carbon Border Adjustment Mechanism data export',
};

const GAP_LINKS = {
  scope1: '/emissions',
  scope2: '/emissions',
  scope3: '/supply-chain',
  waste: '/services/waste',
  water: '/emissions',
  energy: '/emissions',
};

export default function Reports() {
  const [period, setPeriod]   = useState('FY2024-25');
  const [format, setFormat]   = useState('BRSR');
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/emissions/report?format=${format.toLowerCase().replace(' ', '_')}&period=${period}`)
      .then((r) => setReport(r.data))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [period, format]);

  const exportCSV = () => {
    if (!report) return;
    const rows = [
      ['Metric', 'Value', 'Unit'],
      ['Business name', report.business_name, ''],
      ['Reporting period', report.reporting_period, ''],
      ['Scope 1 emissions', report.scope1_tco2e, 'tCO2e'],
      ['Scope 2 emissions', report.scope2_tco2e, 'tCO2e'],
      ['Scope 3 emissions', report.scope3_tco2e, 'tCO2e'],
      ['Total emissions', report.total_tco2e, 'tCO2e'],
      ['Energy consumption', report.energy_consumption_kwh, 'kWh'],
      ['Carbon credits purchased', report.carbon_credits_purchased, 'tCO2e'],
      ['Carbon credits retired', report.carbon_credits_retired, 'tCO2e'],
      ['Carbon score', report.carbon_score, '/100'],
      ['Carbon grade', report.carbon_grade, ''],
      ['Data completeness', report.data_completeness_pct, '%'],
      ['YoY change', report.yoy_change_pct, '%'],
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `carbonx_report_${period}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const copyJSON = () => {
    if (!report) return;
    navigator.clipboard.writeText(JSON.stringify(report, null, 2));
  };

  const gaps = report?.data_gaps || [];

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Compliance Reports — ESG Reports</div>
        <h1>ESG Reporting</h1>
        <p className="section-copy">Generate audit-ready reports aligned with BRSR, GHG Protocol, GRI, and EU CBAM. Auto-filled from your emission logs — gaps highlighted for action.</p>
      </section>

      {/* Report Selector */}
      <section className="surface-panel" style={{ padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label className="form-label" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Period:</label>
            <select className="form-input" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ width: 'auto' }}>
              {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {FORMATS.map((f) => (
              <button key={f} type="button"
                className={`btn btn-sm ${format === f ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setFormat(f)}
                style={{ padding: '0.25rem 0.85rem' }}>
                {f}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>Download PDF</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={exportCSV}>Download CSV</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={copyJSON}>Copy JSON</button>
          </div>
        </div>
        <div style={{ marginTop: '0.75rem', fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: 'var(--muted2)' }}>{FORMAT_DESC[format]}</div>
      </section>

      {loading ? (
        <div className="stat-sub" style={{ padding: '2rem' }}>Generating report…</div>
      ) : !report ? (
        <div className="surface-panel" style={{ padding: '2rem', textAlign: 'center' }}>
          <div className="stat-sub">No data available for this period. <Link to="/emissions" style={{ color: 'var(--neon-cyan)' }}>Log emissions →</Link></div>
        </div>
      ) : (
        <>
          {/* Data Gaps Banner */}
          {gaps.length > 0 && (
            <section className="surface-panel" style={{ padding: '1.25rem 1.5rem', borderLeft: '3px solid var(--neon-yellow)' }}>
              <div className="section-tag" style={{ marginBottom: '0.75rem', color: 'var(--neon-yellow)' }}>Data gaps — {gaps.length} field{gaps.length > 1 ? 's' : ''} require manual input</div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {gaps.map((gap) => (
                  <Link key={gap.field} to={GAP_LINKS[gap.field] || '/emissions'}
                    className="badge"
                    style={{ color: 'var(--neon-yellow)', borderColor: 'var(--neon-yellow)', fontSize: '0.65rem', textDecoration: 'none', padding: '0.2rem 0.6rem' }}>
                    Fill: {gap.label} →
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Report Header */}
          <section className="surface-panel glow-surface" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2rem', alignItems: 'start', marginBottom: '1.25rem' }}>
              <div>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 800, fontSize: '1.2rem' }}>{report.business_name}</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: 'var(--muted2)', marginTop: '0.2rem' }}>Reporting period: {report.reporting_period}</div>
                <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {(report.frameworks || []).map((fw) => (
                    <span key={fw} className="badge" style={{ color: 'var(--neon-cyan)', borderColor: 'var(--neon-cyan)', fontSize: '0.6rem' }}>{fw}</span>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '2rem', color: report.carbon_grade === 'A' ? 'var(--neon-green)' : report.carbon_grade === 'B' ? 'var(--neon-cyan)' : report.carbon_grade === 'C' ? 'var(--neon-yellow)' : 'var(--neon-pink)' }}>
                  Grade {report.carbon_grade}
                </div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted2)' }}>Carbon score: {report.carbon_score}/100</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1.5rem' }}>
              {[
                { label: 'Scope 1', value: `${report.scope1_tco2e} tCO2e`, color: 'var(--neon-orange)', auto: true },
                { label: 'Scope 2', value: `${report.scope2_tco2e} tCO2e`, color: 'var(--neon-yellow)', auto: true },
                { label: 'Scope 3', value: `${report.scope3_tco2e} tCO2e`, color: 'var(--neon-cyan)', auto: true },
                { label: 'Total GHG', value: `${report.total_tco2e} tCO2e`, color: 'var(--text)' },
                { label: 'Credits purchased', value: `${report.carbon_credits_purchased} tCO2e`, color: 'var(--neon-green)', auto: true },
                { label: 'Data completeness', value: `${report.data_completeness_pct}%`, color: report.data_completeness_pct >= 70 ? 'var(--neon-green)' : 'var(--neon-yellow)' },
                { label: 'YoY change', value: report.yoy_change_pct !== null ? `${report.yoy_change_pct > 0 ? '+' : ''}${report.yoy_change_pct}%` : 'N/A', color: report.yoy_change_pct < 0 ? 'var(--neon-green)' : 'var(--neon-pink)' },
              ].map((s) => (
                <div key={s.label}>
                  <div className="stat-label" style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                    {s.label}
                    {s.auto && <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.52rem', color: 'var(--neon-green)', background: 'rgba(0,255,100,0.08)', padding: '0.05rem 0.3rem', borderRadius: '3px' }}>auto</span>}
                  </div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.92rem', color: s.color, marginTop: '0.2rem' }}>{s.value}</div>
                </div>
              ))}
            </div>
          </section>

          {/* BRSR Principle 6 Detail (shown for BRSR format) */}
          {format === 'BRSR' && report.brsr_principle6 && (
            <section className="surface-panel" style={{ padding: '1.5rem' }}>
              <div className="section-tag" style={{ marginBottom: '1.25rem' }}>BRSR Principle 6 — Environmental disclosures</div>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {[
                  { code: 'P6-E1', label: 'Energy consumption', value: report.energy_consumption_kwh ? `${report.energy_consumption_kwh?.toLocaleString('en-IN')} kWh` : null, auto: true },
                  { code: 'P6-E4', label: 'GHG emissions (Scope 1+2+3)', value: `${report.total_tco2e} tCO2e`, auto: true },
                  { code: 'P6-E5', label: 'Energy intensity', value: report.brsr_principle6.energy_intensity_tco2e_per_lakh ? `${report.brsr_principle6.energy_intensity_tco2e_per_lakh} tCO2e / ₹ lakh` : null, auto: true },
                  { code: 'P6-E6', label: 'Waste generated', value: report.brsr_principle6.waste_generated_tonnes ? `${report.brsr_principle6.waste_generated_tonnes} tonnes` : null, auto: true },
                  { code: 'P6-E2', label: 'Water consumption', value: report.brsr_principle6.water_consumption_kl || null, auto: false },
                  { code: 'P6-E3', label: 'Air emissions (NOx, SOx, PM)', value: report.brsr_principle6.air_emissions || null, auto: false },
                  { code: 'P6-E7', label: 'Environmental compliance / penalties', value: report.brsr_principle6.env_compliance || null, auto: false },
                ].map((row) => (
                  <div key={row.code} style={{ display: 'grid', gridTemplateColumns: '70px 1fr auto', gap: '1rem', alignItems: 'center', padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                    <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: 'var(--neon-cyan)' }}>{row.code}</span>
                    <div>
                      <div style={{ fontSize: '0.85rem', marginBottom: '0.15rem' }}>{row.label}</div>
                      {row.value
                        ? <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: 'var(--text)' }}>{row.value}</div>
                        : <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: 'var(--neon-yellow)' }}>— manual entry required</div>
                      }
                    </div>
                    <span className="badge" style={{ fontSize: '0.55rem', color: row.auto && row.value ? 'var(--neon-green)' : row.value ? 'var(--muted2)' : 'var(--neon-yellow)', borderColor: row.auto && row.value ? 'var(--neon-green)' : row.value ? 'var(--border)' : 'var(--neon-yellow)' }}>
                      {row.auto && row.value ? 'Auto-filled' : row.value ? 'Manual' : 'Fill required'}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* YoY Table */}
          {report.yoy && (
            <section className="surface-panel" style={{ padding: '1.5rem' }}>
              <div className="section-tag" style={{ marginBottom: '1rem' }}>Year-on-year comparison</div>
              <table className="data-table">
                <thead><tr><th>Metric</th><th>FY2023-24</th><th>FY2024-25</th><th>Change</th></tr></thead>
                <tbody>
                  {Object.entries(report.yoy).map(([metric, vals]) => (
                    <tr key={metric}>
                      <td style={{ fontSize: '0.82rem' }}>{metric}</td>
                      <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: 'var(--muted2)' }}>{vals.prev ?? '—'}</td>
                      <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem' }}>{vals.curr ?? '—'}</td>
                      <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: vals.change < 0 ? 'var(--neon-green)' : 'var(--neon-pink)' }}>
                        {vals.change !== null ? `${vals.change > 0 ? '+' : ''}${vals.change}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Reduction initiatives count */}
          {report.reduction_initiatives_count > 0 && (
            <section className="surface-panel" style={{ padding: '1.25rem 1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div className="stat-label">Active reduction initiatives</div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1rem', color: 'var(--neon-green)', marginTop: '0.2rem' }}>{report.reduction_initiatives_count} initiatives in progress</div>
                </div>
                <Link to="/roadmap" className="btn btn-secondary btn-sm">View roadmap →</Link>
              </div>
            </section>
          )}

          {report.tokenized_retirements?.length > 0 && (
            <section className="surface-panel" style={{ padding: '1.5rem' }}>
              <div className="section-tag" style={{ marginBottom: '1rem' }}>Tokenized retirement certificates</div>
              <table className="data-table">
                <thead><tr><th>Date</th><th>Project</th><th>Token</th><th>Quantity</th><th>Certificate</th></tr></thead>
                <tbody>
                  {report.tokenized_retirements.map((item) => (
                    <tr key={item.certificate_code}>
                      <td>{new Date(item.created_at).toLocaleDateString('en-IN')}</td>
                      <td>{item.project_name}</td>
                      <td>{item.symbol}</td>
                      <td>{Number(item.quantity).toLocaleString('en-IN')} tCO2e</td>
                      <td>{item.certificate_code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
}
